// Payloads for RSchema operations, and their format validators.
//
// An RSchema is a standalone RObject in its own DAG: the spec for one table
// group. It evolves independently of any group instance; multiple RTableGroup
// instances may share one RSchema. A schema-update op carries ONLY migration
// rules (the slot writes); deploying it is a separate act (each group's
// barrier ref-advance to the new RSchema version).
//
// Spec authority: the create payload names the schema's creators (keyIds plus
// public keys); every schema-update must be signed by one of them. This gates
// spec evolution. Deploy authority is separate: it belongs to each observing
// group, gated by the group's own canDeploy predicate (fixed at group
// creation; see ../rtable_group/payload.ts).
//
// Sub-types shared across RSchema payloads (TableDef, MigrationRule, etc.),
// their json.Format validators, and related model defaults. Semantic
// validation beyond format structure is in validate.ts.
import { json } from "@hyper-hyper-space/hhs3_json";
import { createPayloadTypeFormat } from "@hyper-hyper-space/hhs3_mvt";
// Size limits (payload formats)
export const MAX_NAME_LENGTH = 256;
export const MAX_QUALIFIED_NAME_LENGTH = 2 * MAX_NAME_LENGTH + 1;
export const MAX_SEED_LENGTH = 1024;
export const MAX_HASH_ALGORITHM_LENGTH = 256;
export const MAX_KEY_ID_LENGTH = 256;
export const MAX_SIGNATURE_LENGTH = 8192;
export const MAX_PUBLIC_KEY_LENGTH = 8192;
export const MAX_TABLES = 1024;
export const MAX_COLUMNS = 1024;
export const MAX_FKS = 256;
export const MAX_RESTRICTIONS = 64;
export const MAX_MIGRATION_RULES = 1024;
export const MAX_NOTE_LENGTH = 4096;
export const MAX_CREATORS = 64;
export const MAX_HASH_LENGTH = 128;
export const COLUMN_TYPES = ['string', 'integer', 'float', 'boolean', 'json'];
export const columnDefFormat = {
    type: [json.Type.Union, COLUMN_TYPES.map((t) => [json.Type.Constant, t])],
    nullable: [json.Type.Option, json.Type.Boolean],
    default: [json.Type.Option, json.Type.Something],
    pub: [json.Type.Option, json.Type.Boolean],
    readonly: [json.Type.Option, json.Type.Boolean],
};
// Deletes are permanent (a rowId is a write-once identity; no re-insertion).
// concurrentDeletes governs the delete's reach across concurrency: when true
// (the default) a delete is a barrier that also hides the row at view
// positions concurrent with it (and voids concurrent restriction uses
// witnessing the row); when false the delete acts only causally.
export const DEFAULT_CONCURRENT_DELETES = true;
export const fksFormat = [json.Type.BoundedMap, [json.Type.BoundedString, MAX_NAME_LENGTH], [json.Type.BoundedString, MAX_QUALIFIED_NAME_LENGTH], MAX_FKS];
export const ID_TERMS = ['$author'];
// A `$row.<column>` term references a readonly column of the subject row (the
// row being inserted / updated / deleted). Restricted to readonly columns so
// the value is fixed at insert (merge stability) and reading it never re-enters
// the current op's own value resolution. Row-context only (no subject row in
// 'object' context). Validated structurally (shape) in validate.ts and
// semantically (existing readonly column) in the table-aware checks.
export const ROW_FIELD_PREFIX = '$row.';
// Returns the column name of a `$row.<col>` term, or undefined if `s` is not a
// well-formed row-field term.
export function parseRowFieldTerm(s) {
    if (!s.startsWith(ROW_FIELD_PREFIX))
        return undefined;
    const column = s.substring(ROW_FIELD_PREFIX.length);
    return /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(column) ? column : undefined;
}
export const CMP_OPS = ['eq', 'ne', 'lt', 'le', 'gt', 'ge'];
export const STR_OPS = ['prefix', 'suffix', 'contains'];
export const ARITH_FNS = ['add', 'sub', 'mul'];
export const OP_TAGS = ['insert', 'update', 'delete', 'all'];
// json.Format cannot express recursive structures; predicates are checked with
// validatePredicate in validate.ts. Format fields holding predicates use
// json.Type.Something as a placeholder.
export const restrictionFormat = {
    on: [json.Type.Union, OP_TAGS.map((t) => [json.Type.Constant, t])],
    rule: json.Type.Something,
};
export function defaultRestrictionRule(op) {
    return op === 'insert'
        ? { p: 'true' }
        : { p: 'cmp', cmp: 'eq', left: { col: 'rowAuthor' }, right: { lit: '$author' } };
}
export const idProviderFormat = {
    keyIdColumn: [json.Type.BoundedString, MAX_NAME_LENGTH],
    publicKeyColumn: [json.Type.BoundedString, MAX_NAME_LENGTH],
};
// Returns [groupName, tableName]; groupName is undefined for local references.
export function splitTableRef(ref) {
    const idx = ref.indexOf('.');
    if (idx < 0)
        return [undefined, ref];
    return [ref.substring(0, idx), ref.substring(idx + 1)];
}
export const tableDefFormat = {
    name: [json.Type.BoundedString, MAX_NAME_LENGTH],
    columns: [json.Type.BoundedMap, [json.Type.BoundedString, MAX_NAME_LENGTH], columnDefFormat, MAX_COLUMNS],
    concurrentDeletes: [json.Type.Option, json.Type.Boolean],
    fks: [json.Type.Option, fksFormat],
    restrictions: [json.Type.Option, [json.Type.BoundedArray, restrictionFormat, MAX_RESTRICTIONS]],
    idProvider: [json.Type.Option, idProviderFormat],
};
export const migrationRuleFormat = [json.Type.Union, [
        {
            rule: [json.Type.Constant, 'add-table'],
            def: tableDefFormat,
        },
        {
            rule: [json.Type.Constant, 'drop-table'],
            table: [json.Type.BoundedString, MAX_NAME_LENGTH],
        },
        {
            rule: [json.Type.Constant, 'add-column'],
            table: [json.Type.BoundedString, MAX_NAME_LENGTH],
            column: [json.Type.BoundedString, MAX_NAME_LENGTH],
            def: columnDefFormat,
        },
        {
            rule: [json.Type.Constant, 'drop-column'],
            table: [json.Type.BoundedString, MAX_NAME_LENGTH],
            column: [json.Type.BoundedString, MAX_NAME_LENGTH],
        },
        {
            rule: [json.Type.Constant, 'set-concurrent-deletes'],
            table: [json.Type.BoundedString, MAX_NAME_LENGTH],
            value: json.Type.Boolean,
        },
        {
            rule: [json.Type.Constant, 'set-fks'],
            table: [json.Type.BoundedString, MAX_NAME_LENGTH],
            fks: fksFormat,
        },
        {
            rule: [json.Type.Constant, 'set-restrictions'],
            table: [json.Type.BoundedString, MAX_NAME_LENGTH],
            restrictions: [json.Type.BoundedArray, restrictionFormat, MAX_RESTRICTIONS],
        },
    ]];
export const schemaCreatorFormat = {
    keyId: [json.Type.BoundedString, MAX_KEY_ID_LENGTH],
    publicKey: [json.Type.BoundedString, MAX_PUBLIC_KEY_LENGTH],
};
export const RSCHEMA_TYPE_ID = 'hhs/rschema_v1';
export const createRSchemaFormat = {
    action: [json.Type.Constant, 'create'],
    type: createPayloadTypeFormat(RSCHEMA_TYPE_ID),
    name: [json.Type.BoundedString, MAX_NAME_LENGTH],
    creators: [json.Type.BoundedArray, schemaCreatorFormat, MAX_CREATORS],
    tables: [json.Type.BoundedArray, tableDefFormat, MAX_TABLES],
    hashAlgorithm: [json.Type.Option, [json.Type.BoundedString, MAX_HASH_ALGORITHM_LENGTH]],
};
export const schemaUpdateFormat = {
    action: [json.Type.Constant, 'schema-update'],
    migration: [json.Type.BoundedArray, migrationRuleFormat, MAX_MIGRATION_RULES],
    note: [json.Type.Option, [json.Type.BoundedString, MAX_NOTE_LENGTH]],
    author: [json.Type.BoundedString, MAX_KEY_ID_LENGTH],
    signature: [json.Type.BoundedString, MAX_SIGNATURE_LENGTH],
};
